// The "Getting started" checklist on Home (F7): four first steps for a new studio. The server says which are done
// (GET /workspaces/{ws}/onboarding); this turns that into the rows, and remembers a "Hide" per workspace. Pure,
// except for the storage it is handed.
import { atLeast } from "../lib.ts";
import type { Role } from "../types.ts";

/** What `GET /workspaces/{ws}/onboarding` answers. */
export interface Onboarding {
  workspace_created_at: string;
  first_take_at: string | null;
  shot_made: boolean;
  crew_invited: boolean;
  signoff_asked: boolean;
  claude_connected: boolean;
  done: boolean;
}

export type StartId = "shot" | "crew" | "signoff" | "claude";

export interface StartItem {
  id: StartId;
  label: string;
  /** one plain line under the label */
  hint: string;
  href: string;
  done: boolean;
}

/** The four rows, in the order a new studio meets them. */
export function startItems(o: Onboarding): StartItem[] {
  return [
    {
      id: "shot",
      label: "Make a shot",
      hint: "Describe it and get your first take.",
      href: "#/create",
      done: o.shot_made,
    },
    {
      id: "crew",
      label: "Invite your crew",
      hint: "The people who make takes and sign off.",
      href: "#/settings/members",
      done: o.crew_invited,
    },
    {
      id: "signoff",
      label: "Ask for a sign-off",
      hint: "Send a department to the person who signs it off.",
      href: "#/productions",
      done: o.signoff_asked,
    },
    {
      id: "claude",
      label: "Connect your Claude",
      hint: "Work on your productions from your own Claude.",
      href: "#/settings/claude",
      done: o.claude_connected,
    },
  ];
}

/** "2 of 4 done". */
export const startProgress = (items: readonly StartItem[]): string =>
  `${items.filter((i) => i.done).length} of ${items.length} done`;

/** Owners and producers see the checklist; everyone else has nothing to set up. */
export const canSeeStart = (role: Role | undefined): boolean => atLeast(role, "producer");

export const startHideKey = (ws: string): string => `filmsquare.gettingStarted.hidden.${ws}`;

export interface Store {
  get(key: string): string | null;
  set(key: string, value: string | null): void;
}

export const isStartHidden = (store: Store, ws: string): boolean => store.get(startHideKey(ws)) === "1";
export const hideStart = (store: Store, ws: string): void => store.set(startHideKey(ws), "1");

/** Show it while the server says it is not done, the person may see it, and they have not hidden it. */
export function showStart(o: Onboarding | null, role: Role | undefined, hidden: boolean): boolean {
  return !!o && !o.done && !hidden && canSeeStart(role);
}
